import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ClipboardList } from 'lucide-react';
import clsx from 'clsx';
import { apiRequest, ApiError } from '../lib/api';
import { ListShell } from '../components/ListShell';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { SelectField } from '../components/ui/Field';
import { Pagination } from '../components/people/Pagination';
import { GatedSection } from '../components/shared/GatedSection';
import { formatDate } from '../lib/format';
import {
  ATTEMPT_STATUS_FILTERS,
  AttemptStatusChip,
  PassFailChip,
  type AssessmentAttemptStatus,
  type MyAttemptListItem,
} from '../components/assessments';

export const Route = createFileRoute('/_authed/me/assessments')({
  component: MyAssessmentsPage,
});

const PAGE_SIZE = 20;

interface ListResponse {
  items: MyAttemptListItem[];
  total: number;
  page: number;
  pageSize: number;
}

function MyAssessmentsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<
    'all' | AssessmentAttemptStatus
  >('all');

  const params = useMemo(() => {
    const out: Record<string, string | number | undefined> = {
      page,
      pageSize: PAGE_SIZE,
    };
    if (statusFilter !== 'all') out.status = statusFilter;
    return out;
  }, [page, statusFilter]);

  const query = useQuery({
    queryKey: ['assessments', 'attempts', 'mine', params] as const,
    queryFn: ({ signal }) =>
      apiRequest<ListResponse>('/api/assessments/attempts/mine', {
        signal,
        query: params,
      }),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
    placeholderData: (prev) => prev,
  });

  const items = query.data?.items ?? [];

  return (
    <ListShell
      title="My tests & quizzes"
      description="Your test attempts — hiring tests, rules tests, weekly and monthly quizzes."
    >
      <SelectField
        label="Status"
        value={statusFilter}
        onChange={(e) => {
          setStatusFilter(e.target.value as typeof statusFilter);
          setPage(1);
        }}
        options={ATTEMPT_STATUS_FILTERS.map((o) => ({
          value: o.value,
          label: o.label,
        }))}
        className="max-w-xs"
      />

      <div className="mt-4">
        <GatedSection
          error={query.error}
          title="My tests & quizzes"
          onRetry={() => query.refetch()}
        >
          {query.isLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : items.length === 0 ? (
            <EmptyState
              icon={<ClipboardList size={28} aria-hidden="true" />}
              title="No attempts yet"
              description="When the academy schedules a quiz, hiring test, or rules test for you, it will appear here."
            />
          ) : (
            <>
              <div className="overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)]">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[color:var(--color-surface)]">
                      <tr>
                        <Th>Attempt</Th>
                        <Th>Started</Th>
                        <Th>Status</Th>
                        <Th align="right">Score</Th>
                        <Th>Result</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((a) => (
                        <tr
                          key={a.id}
                          className="cursor-pointer border-t border-[color:var(--color-border)] transition-colors hover:bg-[color:var(--color-surface)]"
                          onClick={() => {
                            if (a.status === 'in_progress') {
                              void navigate({
                                to: '/assessments/attempts/$attemptId',
                                params: { attemptId: a.id },
                              });
                            } else {
                              void navigate({
                                to: '/assessments/attempts/$attemptId/result',
                                params: { attemptId: a.id },
                              });
                            }
                          }}
                        >
                          <td className="px-4 py-2 align-middle">
                            <p className="font-medium">Attempt #{a.attemptNo}</p>
                            <p className="font-mono text-xs text-[color:var(--color-text-muted)]">
                              {a.id.slice(0, 12)}…
                            </p>
                          </td>
                          <td className="px-4 py-2 align-middle text-xs text-[color:var(--color-text-muted)]">
                            {a.startedAt
                              ? formatDate(a.startedAt, { withTime: true })
                              : '—'}
                          </td>
                          <td className="px-4 py-2 align-middle">
                            <AttemptStatusChip status={a.status} />
                          </td>
                          <td className="px-4 py-2 align-middle text-right tabular-nums">
                            {a.scorePercent !== null
                              ? `${a.scorePercent}%`
                              : '—'}
                          </td>
                          <td className="px-4 py-2 align-middle">
                            <PassFailChip pass={a.pass} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <Pagination
                page={page}
                pageSize={PAGE_SIZE}
                total={query.data?.total ?? 0}
                onPageChange={setPage}
              />
            </>
          )}
        </GatedSection>
      </div>
    </ListShell>
  );
}

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
}) {
  return (
    <th
      scope="col"
      className={clsx(
        'px-4 py-2 text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]',
        align === 'right' ? 'text-right' : 'text-left',
      )}
    >
      {children}
    </th>
  );
}
